import { ThunderboltOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, message, Modal, Select, Space, Spin } from "antd";
import { MeterInstallationModeModal } from "components/InstallationMode";
import { isValidNodeMacId } from "components/NodeMapping/NodeMapping";
import useInstallationMode from "hooks/use-installation-mode";
import {
  useCheckMeterMappingLazyQuery,
  useMapCameraMeterMutation,
  useMapCtMeterMutation,
  useUnmapMeterMutation,
  useUpdatePositionConfigurationMutation,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useEffect, useState } from "react";
import NodeMacInput from "../InfrastructureDetails/MappedNodes/MapNodeModal/NodeMacInput";
import { emonSettings } from "../InfrastructureDetails/MappedNodes/MappedNodes.d";

interface CreateMeterModalProps {
  visible: boolean;
  onClose: () => void;
  locationId: string;
  // locationName?: string; // Unused prop - commented out
  preselectedSubjectId?: string | null;
  positions?: any[];
  assets?: any[];
  refetch: () => void;
}

const CreateMeterModal: React.FC<CreateMeterModalProps> = ({
  visible,
  onClose,
  locationId,
  // locationName, // Unused variable - commented out
  preselectedSubjectId,
  positions = [],
  assets = [],
  refetch,
}) => {
  const [meteringForm] = Form.useForm();
  const [meterHardwareType, setMeterHardwareType] = useState<string | null>(null);
  const [cameraNodeMacId, setCameraNodeMacId] = useState<string>("");
  const [ctNodeId, setCtNodeId] = useState<string>("");
  const [hasEnteredSerialOrNode, setHasEnteredSerialOrNode] = useState(false);
  const [nodeIdError, setNodeIdError] = useState<string | null>(null);
  const [ctNodeIdError, setCtNodeIdError] = useState<string | null>(null);
  const [isNodeMappedModalVisible, setIsNodeMappedModalVisible] = useState(false);
  const [mappedNodeInfo, setMappedNodeInfo] = useState<{
    type: "ct" | "camera";
    value: string;
    positionName: string;
    positionId: string;
    mappedChannels?: number[];
    availableChannels?: number[];
  } | null>(null);
  const [isCheckingMapping, setIsCheckingMapping] = useState(false);
  const [existingMeterName, setExistingMeterName] = useState<string | null>(null);
  const [numChannels, setNumChannels] = useState<"single" | "three" | null>(null);
  const [hasSelectedChannelConfig, setHasSelectedChannelConfig] = useState(false);
  const [showInstallationMode, setShowInstallationMode] = useState(false);
  const [newMeterPositionId, setNewMeterPositionId] = useState<string | null>(null);
  const [isNewCtMeter, setIsNewCtMeter] = useState(false);
  const [nodeStatusInfo, setNodeStatusInfo] = useState<{
    nodeStatus?: string;
    nodeStatusInInstallationMode?: string;
    lastTransmission?: string;
    nodeMacId?: string;
  } | null>(null);
  const [hasCompletedInstallation, setHasCompletedInstallation] = useState(false);
  const [suggestedMeterName, setSuggestedMeterName] = useState<string>("");

  // const { user } = useAuth0(); // Unused variable - commented out
  const { setKeyPositionIds } = useInstallationMode();

  const [mapCameraMeter, { loading: mappingCameraMeter }] = useMapCameraMeterMutation();
  const [mapCtMeter, { loading: mappingCtMeter }] = useMapCtMeterMutation();
  const [unmapMeter, { loading: unmappingMeter }] = useUnmapMeterMutation();
  const [updatePositionConfiguration, { loading: updatingPositionConfig }] = useUpdatePositionConfigurationMutation();
  // const [startKeyEntry] = useStartKeyEntryMutation(); // Unused variable - commented out
  // const [endKeyEntry] = useEndKeyEntryMutation(); // Unused variable - commented out

  // Function to generate meter name suggestions based on position or asset name
  const generateMeterNameSuggestion = React.useCallback(
    (subjectId: string | null): string => {
      if (!subjectId) {
        return "";
      }

      // First check if it's an asset
      const asset = assets.find((a: any) => a.id === subjectId);
      if (asset) {
        // Use asset description if available, otherwise use asset type
        const baseName = asset.description || asset.type || asset.name || "Asset";
        const result = `${baseName}_emon`;
        return result;
      }

      // If not an asset, check if it's a position
      const position = positions.find((pos: any) => pos.positionId === subjectId);
      if (position) {
        // Use the position's own name
        const baseName = position.positionName;
        const result = `${baseName}_emon`;
        return result;
      }

      return "";
    },
    [positions, assets]
  );

  // Helper function to get the selected asset's phase information
  const getSelectedAssetPhases = React.useCallback((): number | null => {
    if (!preselectedSubjectId || !assets) return null;

    const selectedAsset = assets.find((asset: any) => asset.id === preselectedSubjectId);
    return selectedAsset?.numOfPhases || null;
  }, [preselectedSubjectId, assets]);

  // Helper function to check if the preselected subject is a zone/position
  const isSelectedSubjectAZone = React.useCallback((): boolean => {
    if (!preselectedSubjectId) return false;

    // Check if it's in the positions array (zones)
    const isPosition = positions.find((pos: any) => pos.positionId === preselectedSubjectId);
    return !!isPosition;
  }, [preselectedSubjectId, positions]);

  // Helper function to check if the preselected subject is an asset
  const isSelectedSubjectAnAsset = React.useCallback((): boolean => {
    if (!preselectedSubjectId) return false;

    // Check if it's in the assets array
    const isAsset = assets.find((asset: any) => asset.id === preselectedSubjectId);
    return !!isAsset;
  }, [preselectedSubjectId, assets]);

  // Helper function to determine if we should skip phase selection
  const shouldSkipPhaseSelection = React.useCallback((): boolean => {
    // For zones, never skip phase selection - always prompt the user
    if (isSelectedSubjectAZone()) {
      return false;
    }

    // For assets, skip only if it's a three-phase asset (existing behavior)
    const assetPhases = getSelectedAssetPhases();
    return assetPhases === 3;
  }, [getSelectedAssetPhases, isSelectedSubjectAZone]);

  const [checkMeterMapping] = useCheckMeterMappingLazyQuery({
    onCompleted: (data) => {
      const mappingStatus = data?.checkMeterMapping;
      if (mappingStatus?.isMapped) {
        const { positionName: originalPositionName = "Unknown", parentPositionName } = mappingStatus;

        setMappedNodeInfo({
          type: isCheckingMapping && cameraNodeMacId ? "camera" : "ct",
          value: isCheckingMapping && cameraNodeMacId ? cameraNodeMacId.trim() : ctNodeId,
          positionName: originalPositionName + (parentPositionName ? ` (in ${parentPositionName})` : ""),
          positionId: mappingStatus.meteringPointPositionId!,
          mappedChannels: mappingStatus.mappedChannels || [],
          availableChannels: mappingStatus.availableChannels || [],
        });
        setIsNodeMappedModalVisible(true);
        setHasEnteredSerialOrNode(false);
      } else if (meterHardwareType === "ct") {
        // For CT meters that are not mapped, check if we should skip channel configuration
        if (shouldSkipPhaseSelection()) {
          // For three-phase assets, skip phase selection and go directly to form
          setNumChannels("three");
          setHasSelectedChannelConfig(true);
          setHasEnteredSerialOrNode(true);
        } else {
          // For single-phase or unknown assets, show channel config step
          setHasEnteredSerialOrNode(true);
          setHasSelectedChannelConfig(false);
        }
      } else {
        setHasEnteredSerialOrNode(true);
      }

      // Store node status information regardless of mapping status
      if (
        mappingStatus &&
        (mappingStatus.nodeStatus || mappingStatus.nodeStatusInInstallationMode || mappingStatus.lastTransmission)
      ) {
        const { nodeStatus, nodeStatusInInstallationMode, lastTransmission } = mappingStatus;
        setNodeStatusInfo({
          nodeStatus: nodeStatus || undefined,
          nodeStatusInInstallationMode: nodeStatusInInstallationMode || undefined,
          lastTransmission: lastTransmission || undefined,
          nodeMacId: isCheckingMapping && cameraNodeMacId ? cameraNodeMacId.trim() : ctNodeId,
        });
      }

      setIsCheckingMapping(false);
    },
    onError: () => {
      message.error("Failed to check meter mapping status");
      setHasEnteredSerialOrNode(false);
      setIsCheckingMapping(false);
    },
  });

  React.useEffect(() => {
    if (meterHardwareType === "ct") {
      meteringForm.setFieldsValue({ type: "energy" });
    }
  }, [meterHardwareType, meteringForm]);

  // Set suggested meter name when modal opens or preselected subject changes
  React.useEffect(() => {
    if (visible && preselectedSubjectId && !existingMeterName && positions && positions.length > 0) {
      const suggestedName = generateMeterNameSuggestion(preselectedSubjectId);
      if (suggestedName) {
        // Try both approaches to set the field
        meteringForm.setFieldsValue({ name: suggestedName });
        setTimeout(() => {
          meteringForm.setFieldValue("name", suggestedName);
          meteringForm.setFieldsValue({ name: suggestedName });
        }, 50);
      }
    }
  }, [visible, preselectedSubjectId, existingMeterName, generateMeterNameSuggestion, meteringForm, positions]);

  // Set suggested meter name when modal opens
  React.useEffect(() => {
    if (visible && preselectedSubjectId && positions && positions.length > 0) {
      const suggestedName = generateMeterNameSuggestion(preselectedSubjectId);
      setSuggestedMeterName(suggestedName);
    } else if (!visible) {
      setSuggestedMeterName("");
    }
  }, [visible, preselectedSubjectId, positions, generateMeterNameSuggestion]);

  React.useEffect(() => {
    if (hasEnteredSerialOrNode && meterHardwareType) {
      if (meterHardwareType === "ct") {
        meteringForm.setFieldsValue({ type: "energy" });
      }
    }
  }, [hasEnteredSerialOrNode, meterHardwareType, meteringForm]);

  React.useEffect(() => {
    if (
      mappedNodeInfo?.positionName &&
      mappedNodeInfo.availableChannels &&
      mappedNodeInfo.availableChannels.length > 0 &&
      hasEnteredSerialOrNode
    ) {
      const positionName = mappedNodeInfo.positionName.split(" (in ")[0];
      setExistingMeterName(positionName);

      setTimeout(() => {
        meteringForm.setFieldValue("name", positionName);
      }, 100);
    } else {
      setExistingMeterName(null);
      // Set suggested name when no existing meter name and we have a preselected position
      if (!mappedNodeInfo && preselectedSubjectId && positions && positions.length > 0) {
        const suggestedName = generateMeterNameSuggestion(preselectedSubjectId);
        if (suggestedName) {
          setTimeout(() => {
            meteringForm.setFieldValue("name", suggestedName);
          }, 150);
        }
      }
    }
  }, [
    mappedNodeInfo,
    hasEnteredSerialOrNode,
    meteringForm,
    preselectedSubjectId,
    positions,
    generateMeterNameSuggestion,
  ]);

  React.useEffect(() => {
    if (existingMeterName) {
      meteringForm.setFieldValue("name", existingMeterName);
    }
  }, [existingMeterName, meteringForm]);

  // Register new meter position ID with installation mode context
  useEffect(() => {
    if (newMeterPositionId && isNewCtMeter) {
      setKeyPositionIds((prevIds: string[]) => {
        if (!prevIds.includes(newMeterPositionId)) {
          return [...prevIds, newMeterPositionId];
        }
        return prevIds;
      });
    }
  }, [newMeterPositionId, isNewCtMeter, setKeyPositionIds]);

  const resetModal = () => {
    setMeterHardwareType(null);
    setCameraNodeMacId("");
    setCtNodeId("");
    setHasEnteredSerialOrNode(false);
    setNodeIdError(null);
    setCtNodeIdError(null);
    setMappedNodeInfo(null);
    setExistingMeterName(null);
    setNumChannels(null);
    setHasSelectedChannelConfig(false);
    setNodeStatusInfo(null);
    setHasCompletedInstallation(false);
    setSuggestedMeterName("");
    meteringForm.resetFields();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const createMeter = async (values: any) => {
    try {
      // Validate that we have required fields
      if (!values.name) {
        throw new Error("Please enter a meter name in the form");
      }

      // Determine subject ID for stream mapping - what the meter is measuring
      // Use preselected subject (asset/position) if available, otherwise default to location
      const subjectId = preselectedSubjectId || locationId;

      // Use the meter name as the position name for the metering point
      const positionName = values.name;

      // Map the meter using the API - backend will handle existing meters automatically
      if (meterHardwareType === "camera") {
        const { data: mappingResult } = await mapCameraMeter({
          variables: {
            locationId,
            positionName,
            serialNumber: cameraNodeMacId,
            streams: [{ subjectId, channelIndex: 0 }],
          },
        });

        if (mappingResult?.mapCameraMeter?.success) {
          message.success(`Camera meter ${cameraNodeMacId} mapped successfully!`);
        } else {
          throw new Error(mappingResult?.mapCameraMeter?.message || "Failed to map camera meter");
        }
      } else if (meterHardwareType === "ct") {
        const { data: mappingResult } = await mapCtMeter({
          variables: {
            locationId,
            positionName,
            nodeMacId: ctNodeId,
            streams: [
              {
                subjectId,
                channelIndex: values.channelIndex,
                numOfPhases: values.numOfPhases || 1,
              },
            ],
          },
        });

        if (mappingResult?.mapCtMeter?.success) {
          const { meteringPointPositionId } = mappingResult.mapCtMeter;

          // Check if this was a new meter (not adding to existing)
          const isNewMeter = !mappedNodeInfo;

          // Use the message from backend which will indicate if it's new or added to existing
          const successMessage = mappingResult.mapCtMeter.message || `CT meter ${ctNodeId} mapped successfully!`;
          message.success(successMessage);

          // Update position configuration with emon settings if provided
          if (meteringPointPositionId && values.ctClampType !== undefined) {
            try {
              const positionConfigUpdate: any = {
                positionId: meteringPointPositionId,
              };

              // Add CT clamp type to desiredEmonSwitchPosition (this controls the CT clamp size/rating)
              if (values.ctClampType !== undefined) {
                positionConfigUpdate.desiredEmonSwitchPosition = values.ctClampType;
              }

              await updatePositionConfiguration({
                variables: {
                  positionConfiguration: positionConfigUpdate,
                },
              });

              message.success("CT clamp configuration updated successfully!");
            } catch (configError: any) {
              // Don't fail the whole operation if position config update fails
              message.warning(
                `Meter mapped successfully, but failed to update CT clamp configuration: ${configError.message}`
              );
            }
          }

          if (isNewMeter && meteringPointPositionId) {
            setNewMeterPositionId(meteringPointPositionId);
            setIsNewCtMeter(true);
            setShowInstallationMode(true);
            // Don't close the modal or refetch yet - let installation mode show first
            return;
          }
        } else {
          throw new Error(mappingResult?.mapCtMeter?.message || "Failed to map CT meter");
        }
      }

      handleClose();
      await refetch();
    } catch (error: any) {
      message.error(error.message || "Failed to create meter");
    }
  };

  const handleMeteringSubmit = async () => {
    try {
      if (meterHardwareType === "camera" && !cameraNodeMacId) {
        message.error("Please enter the Node Mac ID for the Camera Meter.");
        return;
      }
      if (meterHardwareType === "ct" && !ctNodeId) {
        message.error("Please scan or enter a valid Node ID for the CT Meter.");
        return;
      }

      // Ensure we're in the final step where the form fields are rendered
      if (!hasEnteredSerialOrNode) {
        message.error("Please complete the previous steps first.");
        return;
      }

      // For CT meters, check if we need to show channel configuration step first
      if (meterHardwareType === "ct" && !hasSelectedChannelConfig && !mappedNodeInfo) {
        message.error("Please select the channel configuration first.");
        return;
      }

      // For CT meters, validate required fields
      if (meterHardwareType === "ct") {
        const values = await meteringForm.validateFields(["name"]);
        values.type = "energy"; // Ensure type is set

        // Check if this is a three-phase asset or zone mapping
        const assetPhases = getSelectedAssetPhases();
        const isThreePhaseAsset = assetPhases === 3;
        const isZoneMapping = isSelectedSubjectAZone();

        // Set numOfPhases based on configuration
        if (isZoneMapping) {
          // For zones, use user selection from phase configuration
          if (numChannels === "single") {
            values.numOfPhases = 1;
          } else if (numChannels === "three") {
            values.numOfPhases = 3;
          } else {
            values.numOfPhases = 1; // Default fallback for zones
          }
        } else if (assetPhases) {
          // For assets, use asset configuration
          values.numOfPhases = assetPhases;
        } else if (numChannels === "single") {
          values.numOfPhases = 1;
        } else if (numChannels === "three") {
          values.numOfPhases = 3;
        } else {
          values.numOfPhases = 1; // Default fallback
        }

        // Set channel index based on configuration
        if (numChannels === "single") {
          if (isZoneMapping) {
            // For single-phase zone mapping, require channel selection (can use any of the 3 channels)
            const channelValues = await meteringForm.validateFields(["channelIndex"]);
            values.channelIndex = channelValues.channelIndex;
          } else {
            // For single-phase assets, use Channel 2 (index 1)
            values.channelIndex = 1;
          }
        } else if (numChannels === "three") {
          if (isThreePhaseAsset || (isZoneMapping && values.numOfPhases === 3)) {
            // For three-phase assets or three-phase zones, set channelIndex to null since numOfPhases is 3
            values.channelIndex = null;
          } else {
            // For manual three-phase selection (fallback), require channel selection
            const channelValues = await meteringForm.validateFields(["channelIndex"]);
            values.channelIndex = channelValues.channelIndex;
          }
        } else if (mappedNodeInfo?.availableChannels) {
          // For existing meters with available channels, validate channelIndex
          const channelValues = await meteringForm.validateFields(["channelIndex"]);
          values.channelIndex = channelValues.channelIndex;
        }

        // Get optional CT clamp type configuration
        values.ctClampType = meteringForm.getFieldValue("ctClampType");

        await createMeter(values);
        return;
      }

      // For camera meters, validate required fields
      const values = await meteringForm.validateFields(["name", "type"]);
      await createMeter(values);
    } catch (err: any) {
      // Enhanced error logging
      if (err.errorFields) {
        const missingFields = err.errorFields.map((field: any) => field.name[0]).join(", ");
        message.error(`Please fill in the following required fields: ${missingFields}`);
      } else {
        message.error(err.message || "Failed to create meter");
      }
    }
  };

  const handleNodeMacIdNext = () => {
    if (!cameraNodeMacId.trim()) {
      setNodeIdError("Node Mac ID is required.");
      setHasEnteredSerialOrNode(false);
    } else {
      setNodeIdError(null);
      setIsCheckingMapping(true);

      // Check if camera meter is already mapped using the new checkMeterMapping query
      checkMeterMapping({
        variables: {
          identifier: cameraNodeMacId.trim(),
          meterType: "CAMERA" as any,
        },
      });
    }
  };

  const handleCtNodeNext = () => {
    if (!isValidNodeMacId(ctNodeId)) {
      setCtNodeIdError("Invalid Node ID format.");
      setHasEnteredSerialOrNode(false);
      return;
    }
    setCtNodeIdError(null);
    setIsCheckingMapping(true);

    // Check if CT meter is already mapped using the new checkMeterMapping query
    checkMeterMapping({
      variables: {
        identifier: ctNodeId.trim(),
        meterType: "CT" as any,
      },
    });
  };

  const handleUnmapMeter = async () => {
    try {
      const { data: unmapResult } = await unmapMeter({
        variables: {
          meteringPointPositionId: mappedNodeInfo?.positionId!,
          meterType: (mappedNodeInfo?.type === "camera" ? "CAMERA" : "CT") as any,
          identifier: mappedNodeInfo?.value!,
        },
      });

      if (unmapResult?.unmapMeter?.success) {
        message.success(`${mappedNodeInfo?.type === "camera" ? "Camera meter" : "CT meter"} unmapped successfully!`);
        await refetch();
        setIsNodeMappedModalVisible(false);
      } else {
        throw new Error(unmapResult?.unmapMeter?.message || "Failed to unmap meter");
      }
    } catch (error: any) {
      message.error(error.message || "Failed to unmap meter");
    }
  };

  const handleChangeSerialOrNode = () => {
    setIsNodeMappedModalVisible(false);
    setMappedNodeInfo(null);
    if (mappedNodeInfo?.type === "camera") {
      setCameraNodeMacId("");
      setHasEnteredSerialOrNode(false);
    } else {
      setCtNodeId("");
      setHasEnteredSerialOrNode(false);
    }
  };

  const handleInstallationModeComplete = async () => {
    setShowInstallationMode(false);
    setHasCompletedInstallation(true);
    // Don't close the main modal immediately, keep it available for reopening installation mode
    await refetch();
  };

  const toggleInstallationMode = () => {
    setShowInstallationMode(!showInstallationMode);
  };

  let modalContent;
  if (!meterHardwareType) {
    modalContent = (
      <div style={{ textAlign: "center", padding: 24 }}>
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>What meter are you going to use?</div>
        <Button type="primary" style={{ margin: 8, minWidth: 160 }} onClick={() => setMeterHardwareType("camera")}>
          Camera Meter
        </Button>
        <Button type="default" style={{ margin: 8, minWidth: 160 }} onClick={() => setMeterHardwareType("ct")}>
          CT Meter
        </Button>
      </div>
    );
  } else if (!hasEnteredSerialOrNode) {
    modalContent = (
      <>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>Meter Type: </span>
          <span style={{ marginRight: 16 }}>{meterHardwareType === "camera" ? "Camera Meter" : "CT Meter"}</span>
          <Button size="small" type="link" onClick={() => setMeterHardwareType(null)}>
            Change
          </Button>
        </div>
        {meterHardwareType === "camera" && (
          <Form layout="vertical">
            <Form.Item
              label="Node Mac ID"
              required
              validateStatus={nodeIdError ? "error" : undefined}
              help={nodeIdError}
            >
              <Input
                placeholder="Enter Node Mac ID"
                value={cameraNodeMacId}
                onChange={(e) => {
                  setCameraNodeMacId(e.target.value);
                  setNodeIdError(null);
                  setMappedNodeInfo(null);
                }}
                onPressEnter={handleNodeMacIdNext}
              />
              <Button
                type="primary"
                style={{ marginTop: 12, width: "100%" }}
                disabled={!cameraNodeMacId.trim() || isCheckingMapping}
                onClick={handleNodeMacIdNext}
              >
                {isCheckingMapping ? <Spin size="small" /> : "Next"}
              </Button>
            </Form.Item>
          </Form>
        )}
        {meterHardwareType === "ct" && (
          <Form layout="vertical">
            <Form.Item
              label="Scan or Enter Node ID"
              required
              validateStatus={ctNodeIdError ? "error" : undefined}
              help={ctNodeIdError}
            >
              <NodeMacInput
                setNodeMacInput={(val) => {
                  setCtNodeId(val);
                  setCtNodeIdError(null);
                  setHasEnteredSerialOrNode(false);
                  setMappedNodeInfo(null);
                }}
                value={ctNodeId}
                options={{ scan: { isVisible: true }, input: { isVisible: true } }}
                forceSubmit={false}
                isSmartGRD
              />
              <Button
                type="primary"
                style={{ marginTop: 12, width: "100%" }}
                disabled={!ctNodeId.trim() || isCheckingMapping}
                onClick={handleCtNodeNext}
              >
                {isCheckingMapping ? <Spin size="small" /> : "Next"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </>
    );
  } else if (meterHardwareType === "ct" && !hasSelectedChannelConfig && !mappedNodeInfo) {
    // Channel configuration step for new CT meters
    modalContent = (
      <>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>Meter Type: </span>
          <span style={{ marginRight: 16 }}>CT Meter</span>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setHasEnteredSerialOrNode(false);
              setNumChannels(null);
              setHasSelectedChannelConfig(false);
            }}
          >
            Back
          </Button>
        </div>
        <div style={{ textAlign: "center", padding: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
            How many phases does this meter monitor?
          </div>
          <div style={{ marginBottom: 24, color: "#666", fontSize: 14 }}>
            This determines the channel configuration for your CT meter.
          </div>

          {/* Zone-specific messaging */}
          {preselectedSubjectId && isSelectedSubjectAZone() && (
            <div
              style={{
                marginBottom: 24,
                padding: 12,
                backgroundColor: "#e6f7ff",
                borderRadius: 6,
                border: "1px solid #91d5ff",
              }}
            >
              <div style={{ fontSize: 14, color: "#1890ff", fontWeight: 500, marginBottom: 4 }}>
                ℹ Zone/Group Metering
              </div>
              <div style={{ fontSize: 13, color: "#666" }}>
                You&apos;re mapping this CT meter to monitor a zone/group. Please select whether this is a single-phase
                or three-phase electrical system.
              </div>
            </div>
          )}

          {/* Asset-specific messaging */}
          {preselectedSubjectId && isSelectedSubjectAnAsset() && getSelectedAssetPhases() && (
            <div
              style={{
                marginBottom: 24,
                padding: 12,
                backgroundColor: "#f6ffed",
                borderRadius: 6,
                border: "1px solid #b7eb8f",
              }}
            >
              <div style={{ fontSize: 14, color: "#52c41a", fontWeight: 500, marginBottom: 4 }}>
                ℹ Asset Information
              </div>
              <div style={{ fontSize: 13, color: "#666" }}>
                The selected asset is configured for {getSelectedAssetPhases()} phase
                {getSelectedAssetPhases() !== 1 ? "s" : ""}.
                {getSelectedAssetPhases() === 3 && " This selection will be automatically applied."}
              </div>
            </div>
          )}
          <Button
            type="primary"
            style={{ margin: 8, minWidth: 160, display: "block", marginLeft: "auto", marginRight: "auto" }}
            onClick={() => {
              setNumChannels("single");
              setHasSelectedChannelConfig(true);
            }}
          >
            Single Phase
          </Button>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
            {isSelectedSubjectAZone()
              ? "(For single-phase electrical systems - you&apos;ll select which channel to use)"
              : "(Automatically uses Channel 2)"}
          </div>
          <Button
            type="default"
            style={{ margin: 8, minWidth: 160, display: "block", marginLeft: "auto", marginRight: "auto" }}
            onClick={() => {
              setNumChannels("three");
              setHasSelectedChannelConfig(true);
            }}
          >
            Three Phase
          </Button>
          <div style={{ fontSize: 12, color: "#666" }}>
            {isSelectedSubjectAZone()
              ? "(For three-phase electrical systems - uses all three channels)"
              : "(You&apos;ll select which channel to use)"}
          </div>
        </div>
      </>
    );
  } else {
    modalContent = (
      <>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>Meter Type: </span>
          <span style={{ marginRight: 16 }}>{meterHardwareType === "camera" ? "Camera Meter" : "CT Meter"}</span>
          <Button size="small" type="link" onClick={() => setMeterHardwareType(null)}>
            Change
          </Button>
          {meterHardwareType === "ct" && numChannels && !mappedNodeInfo && (
            <>
              <span style={{ marginLeft: 16, fontWeight: 500 }}>Configuration: </span>
              <span style={{ marginRight: 16 }}>
                {numChannels === "single" ? "Single Phase (Channel 2)" : "Three Phase"}
                {isSelectedSubjectAZone() && (
                  <span style={{ fontSize: 12, color: "#666", marginLeft: 8 }}>- Zone Metering</span>
                )}
              </span>
              <Button
                size="small"
                type="link"
                onClick={() => {
                  setHasSelectedChannelConfig(false);
                  setNumChannels(null);
                }}
              >
                Change
              </Button>
            </>
          )}
        </div>
        <Form form={meteringForm} layout="vertical">
          <Form.Item
            label="Meter Name"
            name="name"
            rules={[{ required: true, message: "Please enter a name" }]}
            initialValue={suggestedMeterName || ""}
          >
            <Input
              placeholder={preselectedSubjectId ? "Auto-filled as position name + '_emon'" : "Enter meter name"}
              disabled={mappedNodeInfo?.availableChannels && mappedNodeInfo.availableChannels.length > 0}
              value={existingMeterName || suggestedMeterName || undefined}
              onChange={(e) => {
                if (!existingMeterName) {
                  meteringForm.setFieldValue("name", e.target.value);
                  setSuggestedMeterName(""); // Clear suggested name when user types
                }
              }}
            />
            {mappedNodeInfo?.availableChannels && mappedNodeInfo.availableChannels.length > 0 && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                Using existing meter name from the mapped position: {existingMeterName}
              </div>
            )}
          </Form.Item>
          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: meterHardwareType !== "ct", message: "Please select a type" }]}
            style={{ display: meterHardwareType === "ct" ? "none" : "block" }}
          >
            <Select
              options={[
                { label: "Energy", value: "energy" },
                { label: "Water", value: "water" },
                { label: "Gas", value: "gas" },
              ]}
              placeholder="Select type"
              disabled={meterHardwareType === "ct"}
              onChange={(value) => {
                meteringForm.setFieldValue("type", value);
              }}
            />
          </Form.Item>
          {meterHardwareType === "ct" && (
            <>
              {/* Show channel availability info if we have it */}
              {mappedNodeInfo?.availableChannels && mappedNodeInfo.availableChannels.length > 0 && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: 12,
                    backgroundColor: "#f6ffed",
                    borderRadius: 6,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <div style={{ fontSize: 14, color: "#52c41a", fontWeight: 500, marginBottom: 4 }}>
                    ✓ Available Channels for this CT Node
                  </div>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    Channels {mappedNodeInfo.availableChannels.map((ch) => ch + 1).join(", ")} are available for
                    mapping.
                    {mappedNodeInfo.mappedChannels && mappedNodeInfo.mappedChannels.length > 0 && (
                      <> Channels {mappedNodeInfo.mappedChannels.map((ch) => ch + 1).join(", ")} are already in use.</>
                    )}
                  </div>
                </div>
              )}

              <Form.Item
                label="Channel Index"
                name="channelIndex"
                rules={[{ required: true, message: "Please select a channel index" }]}
                style={{
                  display:
                    (numChannels === "single" && !isSelectedSubjectAZone() && !mappedNodeInfo) ||
                    (numChannels === "three" && shouldSkipPhaseSelection()) ||
                    (numChannels === "three" && isSelectedSubjectAZone())
                      ? "none"
                      : "block",
                }}
              >
                <Select
                  placeholder="Select channel index"
                  options={(() => {
                    // If we have available channels info (from a partially mapped CT), only show those
                    if (mappedNodeInfo?.availableChannels && mappedNodeInfo.availableChannels.length > 0) {
                      return mappedNodeInfo.availableChannels.map((channel) => ({
                        label: `Channel ${channel + 1}`,
                        value: channel,
                      }));
                    }
                    // Otherwise show all channels
                    return [
                      { label: "Channel 1", value: 0 },
                      { label: "Channel 2", value: 1 },
                      { label: "Channel 3", value: 2 },
                    ];
                  })()}
                  onChange={(value) => {
                    meteringForm.setFieldValue("channelIndex", value);
                  }}
                />
              </Form.Item>

              {/* Show channel info for single-phase meters */}
              {numChannels === "single" && !mappedNodeInfo && !isSelectedSubjectAZone() && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: 12,
                    backgroundColor: "#e6f7ff",
                    borderRadius: 6,
                    border: "1px solid #91d5ff",
                  }}
                >
                  <div style={{ fontSize: 14, color: "#1890ff", fontWeight: 500, marginBottom: 4 }}>
                    ℹ Single Phase Configuration
                  </div>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    This meter will automatically use Channel 2 for single-phase monitoring.
                  </div>
                </div>
              )}

              {/* Show info for three-phase assets */}
              {numChannels === "three" && shouldSkipPhaseSelection() && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: 12,
                    backgroundColor: "#f6ffed",
                    borderRadius: 6,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <div style={{ fontSize: 14, color: "#52c41a", fontWeight: 500, marginBottom: 4 }}>
                    ✓ Three Phase Asset Configuration
                  </div>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    This asset is configured for three-phase monitoring. The system will automatically handle the
                    three-phase configuration without requiring manual channel selection.
                  </div>
                </div>
              )}

              {/* Show info for zone/group phase configuration */}
              {isSelectedSubjectAZone() && numChannels && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: 12,
                    backgroundColor: "#e6f7ff",
                    borderRadius: 6,
                    border: "1px solid #91d5ff",
                  }}
                >
                  <div style={{ fontSize: 14, color: "#1890ff", fontWeight: 500, marginBottom: 4 }}>
                    ✓ Zone/Group Phase Configuration
                  </div>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    {numChannels === "single"
                      ? "This CT meter will monitor single-phase power for the selected zone/group. You&apos;ll need to select which channel to use for this measurement."
                      : "This CT meter will monitor three-phase power for the selected zone/group using all three channels for complete three-phase measurement."}
                  </div>
                </div>
              )}

              {/* CT Clamp Type Selection */}
              <Form.Item
                label="CT Clamp Type"
                name="ctClampType"
                tooltip="Select the type of CT clamp being used. This determines the current rating and configures the emon switch position for accurate measurements."
              >
                <Select
                  placeholder="Select CT clamp type (optional)"
                  allowClear
                  options={emonSettings.map((setting) => ({
                    label: setting.label,
                    value: setting.value,
                  }))}
                  onChange={(value) => {
                    meteringForm.setFieldValue("ctClampType", value);
                  }}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </>
    );
  }

  // Show installation mode controls for completed CT meter installation
  if (hasCompletedInstallation && newMeterPositionId && isNewCtMeter) {
    modalContent = (
      <>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>Meter Type: </span>
          <span style={{ marginRight: 16 }}>CT Meter</span>
          <Button size="small" type="link" onClick={() => setMeterHardwareType(null)}>
            Change
          </Button>
        </div>

        <Alert
          message="CT Meter Installation Complete"
          description={`The CT meter "${meteringForm.getFieldValue(
            "name"
          )}" has been successfully installed and configured.`}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* Node Status Information */}
        {nodeStatusInfo && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: "#f6ffed",
              borderRadius: 6,
              border: "1px solid #b7eb8f",
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: 8, color: "#52c41a" }}>Node Status Information</div>
            <div style={{ fontSize: 13, color: "#666" }}>
              <div>
                <strong>Node MAC ID:</strong> {nodeStatusInfo.nodeMacId}
              </div>
              {nodeStatusInfo.nodeStatus && (
                <div>
                  <strong>Status:</strong> {nodeStatusInfo.nodeStatus}
                </div>
              )}
              {nodeStatusInfo.nodeStatusInInstallationMode && (
                <div>
                  <strong>Installation Mode Status:</strong> {nodeStatusInfo.nodeStatusInInstallationMode}
                </div>
              )}
              {nodeStatusInfo.lastTransmission && (
                <div>
                  <strong>Last Transmission:</strong> {new Date(nodeStatusInfo.lastTransmission).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", padding: 24 }}>
          <Space direction="vertical" size="large">
            <Button type="primary" size="large" icon={<ThunderboltOutlined />} onClick={toggleInstallationMode}>
              {showInstallationMode ? "Hide" : "Open"} Installation Mode
            </Button>
            <Button onClick={handleClose}>Close</Button>
          </Space>
        </div>
      </>
    );
  }

  // Installation Mode Modal
  if (showInstallationMode && newMeterPositionId && isNewCtMeter) {
    return (
      <MeterInstallationModeModal
        visible={visible}
        onClose={handleInstallationModeComplete}
        meterPositionId={newMeterPositionId}
        nodeStatusInfo={nodeStatusInfo || undefined}
      />
    );
  }

  return (
    <>
      <Modal
        title="Create Meter"
        open={visible}
        onCancel={handleClose}
        onOk={handleMeteringSubmit}
        okText={(() => {
          if (!meterHardwareType || !hasEnteredSerialOrNode) return undefined;
          if (meterHardwareType === "camera") return "Add";
          if (hasSelectedChannelConfig || mappedNodeInfo) return "Add";
          return undefined;
        })()}
        cancelText="Cancel"
        confirmLoading={mappingCameraMeter || mappingCtMeter || updatingPositionConfig}
        okButtonProps={{
          disabled:
            !hasEnteredSerialOrNode || (meterHardwareType === "ct" && !hasSelectedChannelConfig && !mappedNodeInfo),
        }}
        footer={(() => {
          if (hasCompletedInstallation && newMeterPositionId && isNewCtMeter) return null;
          if (!meterHardwareType || !hasEnteredSerialOrNode) return null;
          if (meterHardwareType === "camera") return undefined;
          if (hasSelectedChannelConfig || mappedNodeInfo) return undefined;
          return null;
        })()}
      >
        {modalContent}
      </Modal>

      <Modal
        title={mappedNodeInfo?.type === "camera" ? "Node Mac ID Already Mapped" : "Node Already Mapped"}
        open={isNodeMappedModalVisible}
        onCancel={() => setIsNodeMappedModalVisible(false)}
        footer={null}
      >
        <div style={{ textAlign: "center", padding: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
            {mappedNodeInfo?.type === "camera" ? (
              <>
                This Node Mac ID <span style={{ color: "#1976d2" }}>{mappedNodeInfo?.value}</span> is already mapped at{" "}
                <span style={{ color: "#1976d2" }}>{mappedNodeInfo?.positionName}</span>.
              </>
            ) : (
              <>
                This node <span style={{ color: "#1976d2" }}>{mappedNodeInfo?.value}</span> is already mapped at{" "}
                <span style={{ color: "#1976d2" }}>{mappedNodeInfo?.positionName}</span>.
              </>
            )}
          </div>

          {mappedNodeInfo?.type === "ct" &&
            mappedNodeInfo?.availableChannels &&
            mappedNodeInfo.availableChannels.length > 0 && (
              <div
                style={{
                  marginBottom: 24,
                  textAlign: "left",
                  backgroundColor: "#f6ffed",
                  padding: 16,
                  borderRadius: 6,
                  border: "1px solid #b7eb8f",
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 8, color: "#52c41a" }}>✓ Channels Available</div>
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  <strong>Mapped channels:</strong>{" "}
                  {mappedNodeInfo.mappedChannels?.map((ch) => `Channel ${ch + 1}`).join(", ") || "None"}
                </div>
                <div style={{ fontSize: 14 }}>
                  <strong>Available channels:</strong>{" "}
                  {mappedNodeInfo.availableChannels.map((ch) => `Channel ${ch + 1}`).join(", ")}
                </div>
              </div>
            )}

          {mappedNodeInfo?.type === "ct" &&
            (!mappedNodeInfo?.availableChannels || mappedNodeInfo.availableChannels.length === 0) && (
              <div
                style={{
                  marginBottom: 24,
                  textAlign: "left",
                  backgroundColor: "#fff2e8",
                  padding: 16,
                  borderRadius: 6,
                  border: "1px solid #ffbb96",
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 8, color: "#fa8c16" }}>⚠ No Channels Available</div>
                <div style={{ fontSize: 14 }}>
                  All channels (1, 2, 3) are already mapped. You need to unmap the existing meter to use this CT node.
                </div>
              </div>
            )}

          <div style={{ marginBottom: 24 }}>
            {mappedNodeInfo?.type === "ct" &&
            mappedNodeInfo?.availableChannels &&
            mappedNodeInfo.availableChannels.length > 0
              ? "Would you like to continue mapping to an available channel, unmap the existing meter, or change node?"
              : `Would you like to unmap or change ${mappedNodeInfo?.type === "camera" ? "Node Mac ID" : "node"}?`}
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            {mappedNodeInfo?.type === "ct" &&
              mappedNodeInfo?.availableChannels &&
              mappedNodeInfo.availableChannels.length > 0 && (
                <Button
                  type="primary"
                  onClick={() => {
                    setIsNodeMappedModalVisible(false);
                    setHasEnteredSerialOrNode(true);

                    // Immediately set the meter name from the existing position
                    if (mappedNodeInfo?.positionName) {
                      const { positionName } = mappedNodeInfo;
                      const positionNameOnly = positionName.split(" (in ")[0];
                      setExistingMeterName(positionNameOnly);
                      setTimeout(() => {
                        meteringForm.setFieldValue("name", positionNameOnly);
                      }, 100);
                    }
                  }}
                >
                  Continue with Available Channel
                </Button>
              )}

            <Button
              type={
                mappedNodeInfo?.type === "ct" &&
                mappedNodeInfo?.availableChannels &&
                mappedNodeInfo.availableChannels.length > 0
                  ? "default"
                  : "primary"
              }
              loading={unmappingMeter}
              onClick={handleUnmapMeter}
            >
              Unmap Existing
            </Button>

            <Button onClick={handleChangeSerialOrNode}>
              Change {mappedNodeInfo?.type === "camera" ? "Node Mac ID" : "Node"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateMeterModal;
